import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import CronEditor from "./components/CronEditor.jsx";
import PropTypes from "prop-types";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import {
  createSourceConfig,
  fetchSourceConfig,
  updateSourceConfig,
} from "@accredo/fe-shared/services/importSourceConfigService.js";
import { fetchConnectors } from "@accredo/fe-shared/services/importConnectorService.js";

const DRIVER_OPTIONS = [
  { value: "local", label: "Lokales Verzeichnis" },
  { value: "sftp", label: "SFTP" },
  { value: "s3", label: "Amazon S3 / S3-kompatibel" },
];

const record_type_OPTIONS = [
  { value: "gesellschaft", label: "Gesellschaft" },
  { value: "kunde", label: "Kunde" },
  { value: "vertrag", label: "Vertrag" },
  { value: "objekt", label: "Objekt" },
  { value: "objektsparte", label: "Objektsparte" },
  { value: "sparte", label: "Sparte" },
  { value: "user", label: "Benutzer" },
  { value: "bank_account", label: "Bankkonto" },
  { value: "user_contract_assignments", label: "Benutzerzuweisungen" },
];

const POST_ACTION_OPTIONS = [
  { value: "archive", label: "Archivieren" },
  { value: "delete", label: "Löschen" },
  { value: "none", label: "Nichts tun" },
];

const DEFAULT_CONNECTION = {
  driver: "local",
  root: "",
  archive_dir: "",
  error_dir: "",
  // SFTP
  host: "",
  port: "22",
  username: "",
  auth_method: "password",
  password: "",
  private_key: "",
  public_key: "",
  passphrase: "",
  // S3
  key: "",
  secret: "",
  region: "",
  bucket: "",
  endpoint: "",
};

const DEFAULT_FORM = {
  import_connector_id: "",
  name: "",
  description: "",
  connection: DEFAULT_CONNECTION,
  streams: [{ pattern: "", record_type: "" }],
  schedule_cron: "",
  post_action: "archive",
  is_active: true,
};

function buildPayload(form) {
  const {
    driver,
    root,
    archive_dir,
    error_dir,
    host,
    port,
    username,
    auth_method,
    password,
    private_key,
    public_key,
    passphrase,
    key,
    secret,
    region,
    bucket,
    endpoint,
  } = form.connection;

  const connection_config = {
    driver,
    root,
    archive_dir: archive_dir || null,
    error_dir: error_dir || null,
  };

  if (driver === "sftp") {
    if (host) connection_config.host = host;
    if (username) connection_config.username = username;
    if (port) connection_config.port = parseInt(port, 10) || 22;
    connection_config.auth_method = auth_method ?? "password";
    if (auth_method === "key") {
      if (private_key) connection_config.private_key = private_key;
      if (public_key) connection_config.public_key = public_key;
      if (passphrase) connection_config.passphrase = passphrase;
    } else {
      if (password) connection_config.password = password;
    }
  }

  if (driver === "s3") {
    if (region) connection_config.region = region;
    if (bucket) connection_config.bucket = bucket;
    if (key) connection_config.key = key;
    if (secret) connection_config.secret = secret;
    if (endpoint) connection_config.endpoint = endpoint;
  }

  return {
    import_connector_id: form.import_connector_id,
    source_type: "file_share",
    name: form.name,
    description: form.description || null,
    connection_config,
    streams: form.streams,
    schedule_cron: form.schedule_cron || null,
    post_action: form.post_action,
    is_active: form.is_active,
  };
}

// ---------------------------------------------------------------------------
// Connection config editor
// ---------------------------------------------------------------------------
function ConnectionConfigEditor({ connection, onChange, isEdit }) {
  const set = (key, value) => onChange({ ...connection, [key]: value });

  function handleDriverChange(new_driver) {
    // Keep path fields; reset driver-specific credentials
    onChange({
      ...DEFAULT_CONNECTION,
      driver: new_driver,
      root: connection.root,
      archive_dir: connection.archive_dir,
      error_dir: connection.error_dir,
    });
  }

  const credential_helper = isEdit
    ? "Leer lassen, um nicht zu ändern"
    : undefined;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <FormControl size="small" sx={{ minWidth: 240 }}>
        <InputLabel>Treiber</InputLabel>
        <Select
          value={connection.driver}
          label="Treiber"
          onChange={(e) => handleDriverChange(e.target.value)}
        >
          {DRIVER_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* SFTP credentials */}
      {connection.driver === "sftp" && (
        <Box display="flex" flexDirection="column" gap={2}>
          {isEdit && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Zugangsdaten werden nicht angezeigt. Nur ausfüllen, wenn sie
              geändert werden sollen.
            </Alert>
          )}
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              label="Host"
              value={connection.host}
              onChange={(e) => set("host", e.target.value)}
              sx={{ flex: 1, minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Port"
              type="number"
              value={connection.port}
              onChange={(e) => set("port", e.target.value)}
              sx={{ width: 90 }}
            />
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
            <TextField
              size="small"
              label="Benutzername"
              value={connection.username}
              onChange={(e) => set("username", e.target.value)}
              sx={{ flex: 1, minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Authentifizierung</InputLabel>
              <Select
                value={connection.auth_method ?? "password"}
                label="Authentifizierung"
                onChange={(e) => set("auth_method", e.target.value)}
              >
                <MenuItem value="password">Passwort</MenuItem>
                <MenuItem value="key">SSH-Schlüssel</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {(connection.auth_method ?? "password") === "password" ? (
            <TextField
              size="small"
              label="Passwort"
              type="password"
              value={connection.password}
              onChange={(e) => set("password", e.target.value)}
              helperText={credential_helper}
              fullWidth
            />
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                size="small"
                label="Privater Schlüssel (PEM)"
                value={connection.private_key}
                onChange={(e) => set("private_key", e.target.value)}
                multiline
                minRows={4}
                fullWidth
                helperText={credential_helper}
                inputProps={{
                  style: { fontFamily: "monospace", fontSize: "0.8rem" },
                }}
                placeholder={"-----BEGIN OPENSSH PRIVATE KEY-----\n..."}
              />
              <TextField
                size="small"
                label="Öffentlicher Schlüssel (optional)"
                value={connection.public_key}
                onChange={(e) => set("public_key", e.target.value)}
                multiline
                minRows={2}
                fullWidth
                helperText={credential_helper}
                inputProps={{
                  style: { fontFamily: "monospace", fontSize: "0.8rem" },
                }}
                placeholder="ssh-rsa AAAA..."
              />
              <TextField
                size="small"
                label="Passphrase (optional)"
                type="password"
                value={connection.passphrase}
                onChange={(e) => set("passphrase", e.target.value)}
                helperText={credential_helper}
                fullWidth
              />
            </Box>
          )}
        </Box>
      )}

      {/* S3 credentials */}
      {connection.driver === "s3" && (
        <Box display="flex" flexDirection="column" gap={2}>
          {isEdit && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Zugangsdaten werden nicht angezeigt. Nur ausfüllen, wenn sie
              geändert werden sollen.
            </Alert>
          )}
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              label="Region"
              placeholder="eu-central-1"
              value={connection.region}
              onChange={(e) => set("region", e.target.value)}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label="Bucket"
              value={connection.bucket}
              onChange={(e) => set("bucket", e.target.value)}
              sx={{ flex: 1, minWidth: 180 }}
            />
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              label="Access Key ID"
              value={connection.key}
              onChange={(e) => set("key", e.target.value)}
              helperText={credential_helper}
              sx={{ flex: 1, minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Secret Access Key"
              type="password"
              value={connection.secret}
              onChange={(e) => set("secret", e.target.value)}
              helperText={credential_helper}
              sx={{ flex: 1, minWidth: 180 }}
            />
          </Box>
          <TextField
            size="small"
            label="Endpoint (optional)"
            placeholder="https://s3.example.com"
            value={connection.endpoint}
            onChange={(e) => set("endpoint", e.target.value)}
            helperText="Für S3-kompatible Dienste (z.B. MinIO). Leer lassen für AWS."
            fullWidth
          />
        </Box>
      )}

      {/* Path fields — always visible */}
      <Box display="flex" gap={2} flexWrap="wrap">
        <TextField
          size="small"
          label={
            connection.driver === "s3" ? "Import-Präfix" : "Importverzeichnis"
          }
          value={connection.root}
          onChange={(e) => set("root", e.target.value)}
          required
          sx={{ flex: 1, minWidth: 220 }}
          inputProps={{ style: { fontFamily: "monospace" } }}
        />
      </Box>
      <Box display="flex" gap={2} flexWrap="wrap">
        <TextField
          size="small"
          label="Archiv-Verzeichnis"
          value={connection.archive_dir}
          onChange={(e) => set("archive_dir", e.target.value)}
          helperText="Erfolgreich importierte Dateien"
          sx={{ flex: 1, minWidth: 200 }}
          inputProps={{ style: { fontFamily: "monospace" } }}
        />
        <TextField
          size="small"
          label="Fehler-Verzeichnis"
          value={connection.error_dir}
          onChange={(e) => set("error_dir", e.target.value)}
          helperText="Dateien mit Importfehlern"
          sx={{ flex: 1, minWidth: 200 }}
          inputProps={{ style: { fontFamily: "monospace" } }}
        />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------
function ImportSourceConfigFormModal({ open, onClose, sourceConfigId }) {
  const isEdit = Boolean(sourceConfigId);
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState(DEFAULT_FORM);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchConnectors()
      .then((res) => setConnectors(res.data ?? []))
      .catch(() =>
        enqueueSnackbar("Connectoren konnten nicht geladen werden", {
          variant: "error",
        })
      );
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    if (!open) return;
    if (!isEdit) {
      setForm(DEFAULT_FORM);
      return;
    }

    setLoading(true);
    fetchSourceConfig(sourceConfigId)
      .then((res) => {
        const c = res.data;
        setForm({
          import_connector_id: c.import_connector_id ?? "",
          name: c.name ?? "",
          description: c.description ?? "",
          connection: {
            ...DEFAULT_CONNECTION,
            driver: c.connection?.driver ?? "local",
            root: c.connection?.root ?? "",
            archive_dir: c.connection?.archive_dir ?? "",
            error_dir: c.connection?.error_dir ?? "",
            auth_method: c.connection?.auth_method ?? "password",
            // credentials (password, private_key, passphrase, key, secret) are never returned by the API — left empty
          },
          streams:
            (c.streams ?? []).length > 0
              ? c.streams
              : [{ pattern: "", record_type: "" }],
          schedule_cron: c.schedule_cron ?? "",
          post_action: c.post_action ?? "archive",
          is_active: c.is_active ?? true,
        });
      })
      .catch(() =>
        enqueueSnackbar("Konfiguration konnte nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setLoading(false));
  }, [open, sourceConfigId, isEdit, enqueueSnackbar]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  function addStream() {
    set("streams", [...form.streams, { pattern: "", record_type: "" }]);
  }

  function removeStream(index) {
    set(
      "streams",
      form.streams.filter((_, i) => i !== index)
    );
  }

  function updateStream(index, key, value) {
    set(
      "streams",
      form.streams.map((s, i) => (i === index ? { ...s, [key]: value } : s))
    );
  }

  function validate() {
    if (!form.import_connector_id) {
      enqueueSnackbar("Connector ist erforderlich", { variant: "warning" });
      return false;
    }
    if (!form.name.trim()) {
      enqueueSnackbar("Name ist erforderlich", { variant: "warning" });
      return false;
    }
    if (!form.connection.root.trim()) {
      enqueueSnackbar("Importverzeichnis ist erforderlich", {
        variant: "warning",
      });
      return false;
    }
    if (form.streams.length === 0) {
      enqueueSnackbar("Mindestens ein Datei-Stream ist erforderlich", {
        variant: "warning",
      });
      return false;
    }

    const invalid = form.streams.findIndex(
      (s) => !s.pattern.trim() || !s.record_type
    );
    if (invalid !== -1) {
      enqueueSnackbar(
        `Stream ${invalid + 1}: Muster und Entitätstyp sind erforderlich`,
        { variant: "warning" }
      );
      return false;
    }

    return true;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload(form);
      isEdit
        ? await updateSourceConfig(sourceConfigId, payload)
        : await createSourceConfig(payload);
      enqueueSnackbar("Konfiguration gespeichert", { variant: "success" });
      onClose(true);
    } catch (err) {
      const msg = err?.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(", ")
        : "Speichern fehlgeschlagen";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="md">
      <DialogTitle>
        {isEdit ? "Import-Quelle bearbeiten" : "Neue Import-Quelle"}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3} pt={1}>
            {/* ── Basics ── */}
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <FormControl
                  size="small"
                  sx={{ minWidth: 220 }}
                  disabled={isEdit}
                >
                  <InputLabel>Connector</InputLabel>
                  <Select
                    value={form.import_connector_id}
                    label="Connector"
                    onChange={(e) => set("import_connector_id", e.target.value)}
                  >
                    {connectors.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name ?? c.slug}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={(e) => set("is_active", e.target.checked)}
                    />
                  }
                  label="Aktiv"
                />
              </Box>

              <TextField
                size="small"
                label="Name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                fullWidth
              />

              <TextField
                size="small"
                label="Beschreibung"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
            </Box>

            <Divider />

            {/* ── Connection ── */}
            <Box>
              <Typography variant="subtitle2" mb={2}>
                Verbindung
              </Typography>
              <ConnectionConfigEditor
                connection={form.connection}
                onChange={(c) => set("connection", c)}
                isEdit={isEdit}
              />
            </Box>

            <Divider />

            {/* ── Streams ── */}
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle2">Datei-Streams</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addStream}
                >
                  Stream hinzufügen
                </Button>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Datei-Muster</TableCell>
                    <TableCell>Entitätstyp</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.streams.map((stream, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ minWidth: 180 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="z.B. kunden_*.csv"
                          value={stream.pattern}
                          onChange={(e) =>
                            updateStream(i, "pattern", e.target.value)
                          }
                          inputProps={{ style: { fontFamily: "monospace" } }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Record Type</InputLabel>
                          <Select
                            value={stream.record_type}
                            label="Record Type"
                            onChange={(e) =>
                              updateStream(i, "record_type", e.target.value)
                            }
                          >
                            {record_type_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeStream(i)}
                          disabled={form.streams.length === 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Divider />

            {/* ── Schedule ── */}
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="subtitle2">Zeitplan</Typography>

              <CronEditor
                value={form.schedule_cron}
                onChange={(cron) => set("schedule_cron", cron)}
              />

              <FormControl
                size="small"
                sx={{ minWidth: 180, alignSelf: "flex-start" }}
              >
                <InputLabel>Nach Import</InputLabel>
                <Select
                  value={form.post_action}
                  label="Nach Import"
                  onChange={(e) => set("post_action", e.target.value)}
                >
                  {POST_ACTION_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box flexGrow={1} />
        <DsButton
          variant="tertiary"
          onClick={() => onClose(false)}
          disabled={saving}
        >
          Abbrechen
        </DsButton>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ImportSourceConfigFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sourceConfigId: PropTypes.number,
};

export default ImportSourceConfigFormModal;
